import React, { useEffect, useState, useCallback } from 'react';
import api from '../../services/api';
import PageHeader from '../../components/common/PageHeader';
import AlertToast from '../../components/common/AlertToast';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const TIER_PALETTE = {
  basic: '#6c757d', starter: '#20c997', pro: '#0d6efd',
  business: '#6f42c1', premium: '#fd7e14', enterprise: '#f59e0b',
};
const tierColor = (tier) => TIER_PALETTE[tier?.toLowerCase()] || '#6c757d';

const blank = {
  name: '', tier: 'basic', description: '', tagline: '',
  price: { monthly: 0, annual: 0 }, currency: 'INR',
  maxUsers: 25, maxStorageMB: 2048,
  features: [''], allowedModuleIds: [],
  highlighted: false, isActive: true, order: 0,
};

export default function SAPlans() {
  const [plans, setPlans]     = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [form, setForm]         = useState(blank);
  const [saving, setSaving]     = useState(false);
  const [toast, setToast]       = useState({ show: false, message: '', type: 'success' });

  const load = useCallback(async () => {
    setLoading(true);
    const [p, m] = await Promise.all([
      api.get('/sa/plans').catch(() => ({ data: { data: [] } })),
      api.get('/sa/modules').catch(() => ({ data: { data: [] } })),
    ]);
    setPlans(p.data?.data || p.data?.records || []);
    setModules(m.data?.data || m.data?.records || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(blank); setEditing(null); setShowForm(true); window.scrollTo(0, 0); };
  const openEdit = (plan) => {
    setForm({
      ...plan,
      features: plan.features?.length ? [...plan.features, ''] : [''],
      allowedModuleIds: (plan.allowedModuleIds || []).map(m => m._id || m),
    });
    setEditing(plan);
    setShowForm(true);
    window.scrollTo(0, 0);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, features: form.features.filter(f => f.trim()) };
    try {
      if (editing) await api.put(`/sa/plans/${editing._id}`, payload);
      else         await api.post('/sa/plans', payload);
      setToast({ show: true, message: `Plan ${editing ? 'updated' : 'created'}!`, type: 'success' });
      setShowForm(false); setEditing(null); load();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || 'Error saving plan', type: 'danger' });
    } finally { setSaving(false); }
  };

  const deletePlan = async (plan) => {
    if (!window.confirm(`Delete "${plan.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/sa/plans/${plan._id}`);
      setToast({ show: true, message: 'Plan deleted', type: 'success' });
      load();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || 'Cannot delete (plan may be in use)', type: 'danger' });
    }
  };

  // Feature list helpers
  const addFeature    = () => setForm(f => ({ ...f, features: [...f.features, ''] }));
  const removeFeature = (i) => setForm(f => ({ ...f, features: f.features.filter((_, idx) => idx !== i) }));
  const setFeature    = (i, v) => setForm(f => ({ ...f, features: f.features.map((x, idx) => idx === i ? v : x) }));

  // Module toggle
  const toggleModule = (id) => setForm(f => {
    const ids = f.allowedModuleIds || [];
    return { ...f, allowedModuleIds: ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id] };
  });
  const selectAllInCat = (mods) => setForm(f => {
    const ids = new Set(f.allowedModuleIds || []);
    mods.forEach(m => ids.add(m._id));
    return { ...f, allowedModuleIds: Array.from(ids) };
  });

  const modulesByCategory = modules.reduce((acc, m) => {
    const cat = m.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(m);
    return acc;
  }, {});

  return (
    <div>
      <PageHeader title="Subscription Plans" icon="bi-credit-card" subtitle="Manage pricing plans, features, and module access"
        actions={<button className="btn btn-primary btn-sm" onClick={openNew}><i className="bi bi-plus-lg me-1"></i>New Plan</button>}
      />

      {/* ── Create / Edit Form ────────────────────────────────────────────── */}
      {showForm && (
        <div className="card mb-4" style={{ border: '2px solid #c7d2fe', borderRadius: 14 }}>
          <div className="card-header d-flex justify-content-between align-items-center" style={{ background: '#f5f3ff', borderBottom: '1px solid #c7d2fe' }}>
            <span style={{ fontWeight: 700, fontSize: 14 }}>
              <i className="bi bi-credit-card me-2 text-primary"></i>
              {editing ? `Edit Plan: ${editing.name}` : 'Create New Plan'}
            </span>
            <button className="btn-close" onClick={() => { setShowForm(false); setEditing(null); }}></button>
          </div>
          <div className="card-body">
            <form onSubmit={handleSave}>
              <div className="row g-3">

                {/* ── Basic Info ───────────────────────────────────────── */}
                <div className="col-12">
                  <div style={{ fontWeight: 600, fontSize: 12, color: '#6366f1', textTransform: 'uppercase', letterSpacing: 1, borderBottom: '1px solid #e0e7ff', paddingBottom: 4 }}>Basic Info</div>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Plan Name <span className="text-danger">*</span></label>
                  <input className="form-control" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required placeholder="e.g. Starter, Pro, Business" />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Tier / Level</label>
                  <input className="form-control" value={form.tier}
                    onChange={e => setForm(f => ({ ...f, tier: e.target.value.toLowerCase().replace(/\s+/g,'') }))}
                    placeholder="basic / pro / enterprise" />
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>Used to determine module access levels</div>
                </div>
                <div className="col-md-2">
                  <label className="form-label">Order</label>
                  <input type="number" className="form-control" value={form.order} onChange={e => setForm(f => ({ ...f, order: Number(e.target.value) }))} />
                </div>
                <div className="col-md-3 d-flex align-items-end gap-3 pb-1">
                  <div className="form-check form-switch">
                    <input className="form-check-input" type="checkbox" id="chk-highlighted" checked={!!form.highlighted} onChange={e => setForm(f => ({ ...f, highlighted: e.target.checked }))} />
                    <label className="form-check-label" htmlFor="chk-highlighted" style={{ fontSize: 13 }}>Mark as "Most Popular"</label>
                  </div>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Description</label>
                  <textarea className="form-control" rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Brief plan description" />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Tagline <span style={{ fontSize: 11, color: '#94a3b8' }}>(shown on pricing page)</span></label>
                  <input className="form-control" value={form.tagline} onChange={e => setForm(f => ({ ...f, tagline: e.target.value }))} placeholder="e.g. Perfect for growing teams" />
                </div>

                {/* ── Pricing & Limits ─────────────────────────────────── */}
                <div className="col-12 mt-1">
                  <div style={{ fontWeight: 600, fontSize: 12, color: '#6366f1', textTransform: 'uppercase', letterSpacing: 1, borderBottom: '1px solid #e0e7ff', paddingBottom: 4 }}>Pricing & Limits</div>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Monthly Price (₹)</label>
                  <input type="number" className="form-control" value={form.price?.monthly}
                    onChange={e => setForm(f => ({ ...f, price: { ...f.price, monthly: Number(e.target.value) } }))} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Annual Price (₹)</label>
                  <input type="number" className="form-control" value={form.price?.annual}
                    onChange={e => setForm(f => ({ ...f, price: { ...f.price, annual: Number(e.target.value) } }))} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Max Users <span style={{ color: '#94a3b8', fontSize: 11 }}>(-1 = unlimited)</span></label>
                  <input type="number" className="form-control" value={form.maxUsers}
                    onChange={e => setForm(f => ({ ...f, maxUsers: Number(e.target.value) }))} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Max Storage MB <span style={{ color: '#94a3b8', fontSize: 11 }}>(-1 = unlimited)</span></label>
                  <input type="number" className="form-control" value={form.maxStorageMB}
                    onChange={e => setForm(f => ({ ...f, maxStorageMB: Number(e.target.value) }))} />
                </div>

                {/* ── Features (dynamic) ───────────────────────────────── */}
                <div className="col-12 mt-1">
                  <div style={{ fontWeight: 600, fontSize: 12, color: '#6366f1', textTransform: 'uppercase', letterSpacing: 1, borderBottom: '1px solid #e0e7ff', paddingBottom: 4, marginBottom: 8 }}>
                    Plan Features <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>(shown as bullet points on pricing page)</span>
                  </div>
                  <div className="d-flex flex-column gap-2" style={{ maxWidth: 600 }}>
                    {form.features.map((feat, i) => (
                      <div className="d-flex gap-2" key={i}>
                        <input className="form-control form-control-sm" value={feat} onChange={e => setFeature(i, e.target.value)}
                          placeholder={`Feature ${i + 1} — e.g. Advanced analytics`} />
                        <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => removeFeature(i)} style={{ padding: '4px 10px' }}>
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    ))}
                    <button type="button" className="btn btn-outline-primary btn-sm align-self-start" onClick={addFeature}>
                      <i className="bi bi-plus-lg me-1"></i>Add Feature
                    </button>
                  </div>
                </div>

                {/* ── Module Access ─────────────────────────────────────── */}
                <div className="col-12 mt-1">
                  <div style={{ fontWeight: 600, fontSize: 12, color: '#6366f1', textTransform: 'uppercase', letterSpacing: 1, borderBottom: '1px solid #e0e7ff', paddingBottom: 4, marginBottom: 8 }}>
                    Included Modules <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>({(form.allowedModuleIds || []).length} of {modules.length} selected)</span>
                  </div>
                  {Object.entries(modulesByCategory).map(([cat, mods]) => (
                    <div key={cat} className="mb-3">
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <span style={{ fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase' }}>{cat}</span>
                        <button type="button" className="btn btn-link p-0" style={{ fontSize: 11 }} onClick={() => selectAllInCat(mods)}>select all</button>
                      </div>
                      <div className="d-flex flex-wrap gap-1">
                        {mods.map(m => {
                          const selected = (form.allowedModuleIds || []).includes(m._id);
                          return (
                            <button type="button" key={m._id}
                              className={`btn btn-sm ${selected ? 'btn-primary' : 'btn-outline-secondary'}`}
                              style={{ fontSize: 11, padding: '2px 10px' }}
                              onClick={() => toggleModule(m._id)}
                            >
                              <i className={`${m.icon || 'bi bi-grid'} me-1`}></i>{m.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="d-flex gap-2 justify-content-end mt-3 pt-3 border-top">
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setShowForm(false); setEditing(null); }}>Cancel</button>
                <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
                  {saving && <span className="spinner-border spinner-border-sm me-1"></span>}
                  {editing ? 'Update Plan' : 'Create Plan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Plans Grid ────────────────────────────────────────────────────── */}
      {loading ? <LoadingSpinner /> : (
        <div className="row g-3">
          {plans.map(plan => (
            <div className="col-lg-4 col-md-6" key={plan._id}>
              <div className="card h-100" style={{ borderRadius: 14, borderWidth: plan.highlighted ? 2 : 1, borderColor: plan.highlighted ? '#6366f1' : '#e2e8f0' }}>
                {plan.highlighted && (
                  <div className="card-header text-center py-1 text-white" style={{ background: '#6366f1', fontSize: 11, borderRadius: '12px 12px 0 0', fontWeight: 600, letterSpacing: 1 }}>
                    ★ MOST POPULAR
                  </div>
                )}
                <div className="card-body d-flex flex-column">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div>
                      <h5 className="fw-bold mb-1">{plan.name}</h5>
                      <span className="badge" style={{ background: tierColor(plan.tier) + '20', color: tierColor(plan.tier), fontWeight: 600 }}>
                        {plan.tier}
                      </span>
                      {plan.tagline && <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>{plan.tagline}</div>}
                    </div>
                    <div className="text-end">
                      <div className="fw-bold" style={{ fontSize: 22 }}>₹{(plan.price?.monthly || 0).toLocaleString()}</div>
                      <div style={{ fontSize: 11, color: '#6c757d' }}>/ month</div>
                      {plan.price?.annual > 0 && (
                        <div style={{ fontSize: 10, color: '#22c55e' }}>₹{(plan.price.annual).toLocaleString()} / year</div>
                      )}
                    </div>
                  </div>

                  <div className="d-flex gap-3 mb-3" style={{ fontSize: 12, color: '#64748b' }}>
                    <span><i className="bi bi-people me-1"></i>{plan.maxUsers === -1 ? '∞' : plan.maxUsers} users</span>
                    <span><i className="bi bi-hdd me-1"></i>{plan.maxStorageMB === -1 ? '∞' : `${(plan.maxStorageMB / 1024).toFixed(0)} GB`}</span>
                    <span><i className="bi bi-grid me-1"></i>{plan.allowedModuleIds?.length || 0} modules</span>
                  </div>

                  {(plan.features || []).length > 0 && (
                    <ul className="list-unstyled mb-3 flex-grow-1" style={{ fontSize: 12 }}>
                      {plan.features.map((f, i) => (
                        <li key={i} className="mb-1"><i className="bi bi-check2 text-success me-1"></i>{f}</li>
                      ))}
                    </ul>
                  )}

                  <div className="d-flex gap-1 mt-auto pt-2 border-top">
                    <button className="btn btn-outline-primary btn-sm flex-grow-1" onClick={() => openEdit(plan)}>
                      <i className="bi bi-pencil me-1"></i>Edit
                    </button>
                    <button className="btn btn-outline-danger btn-sm" onClick={() => deletePlan(plan)} style={{ padding: '4px 12px' }}>
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
          {plans.length === 0 && (
            <div className="col-12 text-center py-5 text-muted">
              <i className="bi bi-credit-card display-4 d-block mb-3 opacity-25"></i>
              No plans yet. <button className="btn btn-link p-0" onClick={openNew}>Create your first plan</button>
            </div>
          )}
        </div>
      )}

      <AlertToast {...toast} onClose={() => setToast(t => ({ ...t, show: false }))} />
    </div>
  );
}
